import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { RocSignalLabel, RocCorner } from "@/components/roc/RocSignalLabel";
import { RevealBlock, StaggerGroup, StaggerItem } from "@/components/motion/primitives";
import { esRoutes, enRoutes } from "@/utils/routes";

interface AboutDossierProps {
  language: 'es' | 'en';
}

const PORTRAIT = "/media/luis-roc/portrait.png";

/**
 * SEARCH STRATEGIST DOSSIER — ROC Search Interface System.
 * Perfil profesional sin métricas, clientes, conferencias ni cifras de experiencia:
 * solo afirmaciones VERIFIED en SEO-EVIDENCE-REGISTER.md.
 */
export const AboutDossier = ({ language }: AboutDossierProps) => {
  const routes = language === 'es' ? esRoutes : enRoutes;

  const t = language === 'es'
    ? {
        eyebrow: "Perfil",
        title: "Luis ROC",
        role: "Consultor de SEO y Search Growth",
        lead:
          "Fundador de ROC Worldwide Agency LLC. Más de 25 años de experiencia, trabajando en marketing digital desde el año 2000. Trabajo con empresas B2B y marcas de ecommerce que necesitan criterio senior en búsqueda: qué priorizar, por qué, y cómo saber si funcionó.",
        body: [
          "Mi trabajo se sitúa en la intersección entre el sistema de búsqueda (rastreo, indexación, intención, autoridad) y la decisión de negocio que hay detrás. No vendo horas de ejecución genérica ni entrego listas de tareas sin criterio de prioridad.",
          "Trabajo en español y en inglés, en proyectos que pueden involucrar distintos mercados, idiomas y entornos de búsqueda: empresas de EE. UU. que quieren llegar al consumidor hispano, o marcas que necesitan competir en más de un país sin duplicar contenido traducido.",
        ],
        signalTitle: "Ejes del trabajo",
        signal: [
          { k: "Search", v: "Cómo un sistema de búsqueda encuentra, entiende y prioriza lo que ya existe en el sitio." },
          { k: "Strategy", v: "Qué se hace primero y qué se descarta, según impacto, esfuerzo y evidencia disponible." },
          { k: "Business", v: "Qué señal del negocio confirma que el trabajo tuvo el efecto esperado." },
        ],
        expertiseTitle: "Áreas de expertise",
        expertise: [
          { label: "SEO técnico", text: "Rastreo, indexación, renderizado y arquitectura en sitios grandes y migraciones." },
          { label: "SEO internacional", text: "Estructura multi-mercado, hreflang y decisiones de dominio por país o idioma." },
          { label: "Búsqueda bilingüe", text: "Estrategia ES/EN para el consumidor hispano de EE. UU., sin traducción literal." },
          { label: "Ecommerce", text: "Arquitectura de categoría frente a producto, facetas y catálogos grandes." },
          { label: "Amazon", text: "Listings y relación entre orgánico de Amazon y Amazon Advertising." },
          { label: "AI Search", text: "Estructura de contenido y señales para buscadores generativos y asistentes." },
        ],
        philosophyTitle: "Punto de vista",
        philosophy: [
          "El SEO no falla por falta de tareas, falla por falta de prioridad.",
          "Una hipótesis sin forma de medirla no es una estrategia.",
          "Si no se puede documentar, no se publica como resultado.",
          "El mercado hispano no es una traducción del mercado en inglés.",
        ],
        linksTitle: "Continuar",
        links: [
          { to: routes.methodology, label: "ROC Search Growth Framework", desc: "Cómo se prioriza, ejecuta y mide el trabajo." },
          { to: routes.library, label: "Search Library", desc: "Artículos y guías sobre búsqueda, prioridad y AI Search." },
          { to: routes.services, label: "Capacidades de búsqueda", desc: "El ecosistema de servicios y cómo se conectan." },
        ],
        linkedin: "Perfil de LinkedIn",
        portraitAlt: "Retrato profesional de Luis ROC, consultor de SEO y Search Growth",
        ctaLabel: "Solicitar consultoría estratégica",
        ctaNote: "Revisamos tu situación y qué priorizar. Sin compromiso.",

      }
    : {
        eyebrow: "Profile",
        title: "Luis ROC",
        role: "SEO & Search Growth Consultant",
        lead:
          "Founder of ROC Worldwide Agency LLC. 25+ years of experience, working in digital marketing since 2000. I work with B2B companies and ecommerce brands that need senior judgement in search: what to prioritize, why, and how to know whether it worked.",
        body: [
          "My work sits where the search system (crawling, indexing, intent, authority) meets the business decision behind it. I don't sell generic execution hours or hand over task lists with no prioritization logic.",
          "I work in Spanish and English, on engagements that can span markets, languages and search environments: US companies reaching the Hispanic consumer, or brands that need to compete in more than one country without duplicating translated content.",
        ],
        signalTitle: "Working axes",
        signal: [
          { k: "Search", v: "How a search system finds, understands and prioritizes what already exists on the site." },
          { k: "Strategy", v: "What gets done first and what gets dropped, based on impact, effort and available evidence." },
          { k: "Business", v: "Which business signal confirms the work had the intended effect." },
        ],
        expertiseTitle: "Areas of expertise",
        expertise: [
          { label: "Technical SEO", text: "Crawling, indexing, rendering and architecture on large sites and migrations." },
          { label: "International SEO", text: "Multi-market structure, hreflang and domain decisions per country or language." },
          { label: "Bilingual search", text: "ES/EN strategy for the US Hispanic consumer, without literal translation." },
          { label: "Ecommerce", text: "Category vs. product architecture, facets and large catalogs." },
          { label: "Amazon", text: "Listings and the relationship between Amazon organic and Amazon Advertising." },
          { label: "AI Search", text: "Content structure and signals for generative search engines and assistants." },
        ],
        philosophyTitle: "Point of view",
        philosophy: [
          "SEO rarely fails from a lack of tasks — it fails from a lack of priority.",
          "A hypothesis with no way to measure it isn't a strategy.",
          "If it can't be documented, it isn't published as a result.",
          "The Hispanic market is not a translation of the English-language market.",
        ],
        linksTitle: "Continue",
        links: [
          { to: routes.methodology, label: "ROC Search Growth Framework", desc: "How work is prioritized, executed and measured." },
          { to: routes.library, label: "Search Library", desc: "Articles and guides on search, priority and AI Search." },
          { to: routes.services, label: "Search capabilities", desc: "The service ecosystem and how the parts connect." },
        ],
        linkedin: "LinkedIn profile",
        portraitAlt: "Professional portrait of Luis ROC, SEO and Search Growth consultant",
        ctaLabel: "Request a strategy consultation",
        ctaNote: "We review your situation and what to prioritize. No commitment.",

      };

  return (
    <>
      {/* DOSSIER HEADER */}
      <section className="relative overflow-hidden border-b border-slate-800 bg-slate-950 py-16 lg:py-24">
        <div className="container-custom">
          <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
            <div className="min-w-0">
              <RocSignalLabel index="00" tone="invert">{t.eyebrow}</RocSignalLabel>
              <h1 className="font-display mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-white lg:text-6xl">
                {t.title}
              </h1>
              <p className="mt-4 font-mono text-[12px] uppercase tracking-[0.22em] text-brand">{t.role}</p>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 lg:text-lg">{t.lead}</p>
              <div className="mt-6 max-w-2xl space-y-4">
                {t.body.map((p) => (
                  <p key={p} className="text-sm leading-relaxed text-slate-400 lg:text-base">{p}</p>
                ))}
              </div>
              <a
                href="https://www.linkedin.com/in/luisroc/"
                target="_blank"
                rel="noopener noreferrer"
                className="roc-link mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                {t.linkedin}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>

            <RevealBlock direction="left" className="relative min-w-0">
              <RocCorner className="-left-2 -top-2 border-l border-t" />
              <RocCorner className="-bottom-2 -right-2 border-b border-r" />
              <div className="relative overflow-hidden">
                <img
                  src={PORTRAIT}
                  alt={t.portraitAlt}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full object-cover object-top grayscale-[0.15] sm:aspect-[3/4] lg:aspect-auto"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/6 bg-[linear-gradient(to_bottom,theme(colors.slate.950/0.55)_0%,transparent_100%)]"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-[linear-gradient(to_top,theme(colors.slate.950)_0%,theme(colors.slate.950/0.85)_35%,transparent_100%)] lg:h-1/3"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-[linear-gradient(to_right,theme(colors.slate.950/0.75)_0%,transparent_100%)] lg:w-1/5 lg:bg-[linear-gradient(to_right,theme(colors.slate.950/0.65)_0%,transparent_100%)]"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-[linear-gradient(to_left,theme(colors.slate.950/0.75)_0%,transparent_100%)] lg:w-1/5 lg:bg-[linear-gradient(to_left,theme(colors.slate.950/0.65)_0%,transparent_100%)]"
                />
              </div>

              <div className="mt-6">
                <Link
                  to={routes.booking}
                  className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 sm:w-auto"
                >
                  {t.ctaLabel}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <p className="mt-3 text-xs leading-relaxed text-slate-400">{t.ctaNote}</p>
              </div>



            </RevealBlock>
          </div>
        </div>
      </section>

      {/* SEARCH / STRATEGY / BUSINESS */}
      <section className="border-b border-border bg-background py-14 lg:py-20">
        <div className="container-custom">
          <RocSignalLabel index="01">{t.signalTitle}</RocSignalLabel>
          <StaggerGroup as="ul" className="mt-8 border-t border-border">
            {t.signal.map((row, i) => (
              <StaggerItem as="li" key={row.k} className="border-b border-border">
                <div className="grid gap-2 py-6 sm:grid-cols-[64px_200px_1fr] sm:gap-6">
                  <span className="font-mono text-[11px] text-brand">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-display text-lg font-semibold text-foreground">{row.k}</span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{row.v}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="border-b border-border bg-background py-14 lg:py-20">
        <div className="container-custom">
          <RocSignalLabel index="02">{t.expertiseTitle}</RocSignalLabel>
          <h2 className="font-display mt-4 max-w-2xl text-2xl font-bold tracking-tight text-foreground lg:text-3xl">
            {t.expertiseTitle}
          </h2>
          <StaggerGroup as="ul" className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {t.expertise.map((item) => (
              <StaggerItem as="li" key={item.label} className="bg-background p-6">
                <span className="h-px w-6 bg-brand" aria-hidden="true" />
                <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.08em] text-foreground">{item.label}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* POINT OF VIEW */}
      <section className="border-b border-slate-800 bg-slate-950 py-14 lg:py-20">
        <div className="container-custom">
          <RocSignalLabel index="03" tone="invert">{t.philosophyTitle}</RocSignalLabel>
          <ul className="mt-8 grid gap-px border border-slate-800 bg-slate-800 md:grid-cols-2">
            {t.philosophy.map((p) => (
              <li key={p} className="bg-slate-950 p-7">
                <p className="font-display text-lg leading-snug text-white lg:text-xl">{p}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTINUE */}
      <section className="border-b border-border bg-background py-14 lg:py-20">
        <div className="container-custom">
          <RocSignalLabel index="04">{t.linksTitle}</RocSignalLabel>
          <ul className="mt-8 border-t border-border">
            {t.links.map((link) => (
              <li key={link.to} className="border-b border-border">
                <Link
                  to={link.to}
                  className="group flex flex-col gap-1 py-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:flex-row sm:items-baseline sm:gap-8"
                >
                  <span className="flex items-center gap-2 text-base font-semibold text-foreground sm:w-[320px]">
                    {link.label}
                    <ArrowRight className="h-4 w-4 text-brand transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{link.desc}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};
